import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  FileText, MessageSquare, Settings, Plus, Pencil, Trash2, Eye, EyeOff,
  LogOut, BarChart3, Users, Upload, Briefcase, Scale, Building2, Wifi, WifiOff, UserPlus, UserMinus, Check, X, Loader2, FolderOpen
} from 'lucide-react';
import api, { formatApiError } from '../lib/api';
import ImageUploader from '../components/ImageUploader';
import AdminDocuments from '../components/AdminDocuments';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Article Editor
function ArticleEditor({ article, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: article?.title || '',
    excerpt: article?.excerpt || '',
    content: article?.content || '',
    featured_image: article?.featured_image || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-[#1A1F2C] font-medium">Cím *</Label>
        <Input
          data-testid="article-title-input"
          value={form.title}
          onChange={e => setForm({ ...form, title: e.target.value })}
          required
          className="mt-1.5 border-[#E2E8F0]"
        />
      </div>
      <div>
        <Label className="text-[#1A1F2C] font-medium">Kivonat *</Label>
        <Textarea
          data-testid="article-excerpt-input"
          value={form.excerpt}
          onChange={e => setForm({ ...form, excerpt: e.target.value })}
          required
          rows={2}
          className="mt-1.5 border-[#E2E8F0]"
        />
      </div>
      <div>
        <Label className="text-[#1A1F2C] font-medium">Tartalom (HTML) *</Label>
        <Textarea
          data-testid="article-content-input"
          value={form.content}
          onChange={e => setForm({ ...form, content: e.target.value })}
          required
          rows={8}
          className="mt-1.5 border-[#E2E8F0] font-mono text-sm"
        />
      </div>
      <ImageUploader
        value={form.featured_image}
        onChange={(url) => setForm(f => ({ ...f, featured_image: url }))}
        label="Kiemelt kép"
      />
      <div className="flex gap-3 pt-4">
        <Button type="submit" disabled={saving} data-testid="article-save-btn"
          className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">
          {saving ? 'Mentés...' : (article ? 'Frissítés' : 'Létrehozás')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="border-[#E2E8F0]">
          Mégse
        </Button>
      </div>
    </form>
  );
}

// Service Editor
function ServiceEditor({ service, onSave, onCancel }) {
  const [form, setForm] = useState({
    tier: service?.tier || 'bronze',
    name: service?.name || '',
    description: service?.description || '',
    features: service?.features?.join('\n') || '',
    highlighted: service?.highlighted || false,
    price_label: service?.price_label || '',
    order: service?.order || 0
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...form,
        features: form.features.split('\n').filter(f => f.trim()),
        order: parseInt(form.order) || 0
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-[#1A1F2C] font-medium">Szint</Label>
          <select
            data-testid="service-tier-select"
            value={form.tier}
            onChange={e => setForm({ ...form, tier: e.target.value })}
            className="mt-1.5 w-full p-2 border border-[#E2E8F0] rounded-lg text-sm"
          >
            <option value="bronze">Bronz</option>
            <option value="silver">Ezüst</option>
            <option value="gold">Arany</option>
            <option value="vip">VIP</option>
          </select>
        </div>
        <div>
          <Label className="text-[#1A1F2C] font-medium">Sorrend</Label>
          <Input
            type="number"
            value={form.order}
            onChange={e => setForm({ ...form, order: e.target.value })}
            className="mt-1.5 border-[#E2E8F0]"
          />
        </div>
      </div>
      <div>
        <Label className="text-[#1A1F2C] font-medium">Név *</Label>
        <Input
          data-testid="service-name-input"
          value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          required
          className="mt-1.5 border-[#E2E8F0]"
        />
      </div>
      <div>
        <Label className="text-[#1A1F2C] font-medium">Leírás *</Label>
        <Textarea
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          required
          rows={3}
          className="mt-1.5 border-[#E2E8F0]"
        />
      </div>
      <div>
        <Label className="text-[#1A1F2C] font-medium">Funkciók (soronként 1)</Label>
        <Textarea
          data-testid="service-features-input"
          value={form.features}
          onChange={e => setForm({ ...form, features: e.target.value })}
          rows={5}
          className="mt-1.5 border-[#E2E8F0]"
          placeholder="Soronként egy funkció"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-[#1A1F2C] font-medium">Ár címke</Label>
          <Input
            value={form.price_label}
            onChange={e => setForm({ ...form, price_label: e.target.value })}
            className="mt-1.5 border-[#E2E8F0]"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.highlighted}
              onChange={e => setForm({ ...form, highlighted: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-sm text-[#1A1F2C]">Kiemelt csomag</span>
          </label>
        </div>
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="submit" disabled={saving} data-testid="service-save-btn"
          className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">
          {saving ? 'Mentés...' : (service ? 'Frissítés' : 'Létrehozás')}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="border-[#E2E8F0]">
          Mégse
        </Button>
      </div>
    </form>
  );
}


// ==================== COMPANIES TAB ====================

function CompaniesTab({ companies, allUsers, onSave, onDelete, onReload, editingCompany, setEditingCompany, showEditor, setShowEditor }) {
  const [assignEmail, setAssignEmail] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const deriveTaxCore = (taxNum) => {
    const digits = (taxNum || '').replace(/\D/g, '');
    return digits.substring(0, 8);
  };

  const handleTestConnection = async (companyId) => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.get(`/novitax/test/${companyId}`);
      setTestResult(data);
    } catch (err) {
      setTestResult({ connected: false, error: err.response?.data?.detail || 'Hiba történt' });
    } finally {
      setTesting(false);
    }
  };

  const handleAssign = async (companyId) => {
    if (!assignEmail.trim()) return;
    try {
      await api.post(`/admin/companies/${companyId}/assign`, { user_email: assignEmail.trim() });
      setAssignEmail('');
      onReload();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba');
    }
  };

  const handleUnassign = async (companyId, email) => {
    try {
      await api.post(`/admin/companies/${companyId}/unassign`, { user_email: email });
      onReload();
    } catch (err) {
      alert(err.response?.data?.detail || 'Hiba');
    }
  };

  if (showEditor) {
    const co = editingCompany || {};
    const taxCore = deriveTaxCore(co.tax_number || '');
    return (
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 sm:p-8">
        <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D] mb-4">
          {co.id ? 'Cég szerkesztése' : 'Új cég'}
        </h3>
        <form onSubmit={async (e) => {
          e.preventDefault();
          await onSave({
            company_name: co.company_name || '',
            tax_number: co.tax_number || '',
            novitax_db_uid: co.novitax_db_uid || '',
            novitax_enabled: co.novitax_enabled || false
          });
        }} className="space-y-4">
          <div>
            <Label className="text-[#1A1F2C] font-medium">Cégnév *</Label>
            <Input
              data-testid="company-name-input"
              value={co.company_name || ''}
              onChange={e => setEditingCompany({ ...co, company_name: e.target.value })}
              required placeholder="Pl. Példa Kft."
              className="mt-1.5 border-[#E2E8F0]"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#1A1F2C] font-medium">Adószám *</Label>
              <Input
                data-testid="company-taxnumber-input"
                value={co.tax_number || ''}
                onChange={e => setEditingCompany({ ...co, tax_number: e.target.value })}
                required placeholder="12345678-2-41"
                className="mt-1.5 border-[#E2E8F0]"
              />
              <p className="text-xs text-[#64748B] mt-1">
                Adószámtörzs (automatikus): <span className="font-mono font-semibold text-[#0B1B3D]">{taxCore || '—'}</span>
                {taxCore.length === 8 && <Check size={12} className="inline ml-1 text-green-500" />}
                {taxCore.length > 0 && taxCore.length < 8 && <span className="text-red-500 ml-1">({taxCore.length}/8)</span>}
              </p>
            </div>
            <div>
              <Label className="text-[#1A1F2C] font-medium">Novitax DB UID</Label>
              <Input
                data-testid="company-dbuid-input"
                value={co.novitax_db_uid || ''}
                onChange={e => setEditingCompany({ ...co, novitax_db_uid: e.target.value })}
                placeholder="Novitax-tól kapott UID"
                className="mt-1.5 border-[#E2E8F0]"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-[#FAFAFA] rounded-lg border border-[#E2E8F0]">
            <input
              type="checkbox"
              data-testid="company-novitax-toggle"
              checked={co.novitax_enabled || false}
              onChange={e => setEditingCompany({ ...co, novitax_enabled: e.target.checked })}
              className="w-4 h-4"
            />
            <div>
              <span className="text-sm font-medium text-[#1A1F2C]">Novitax hozzáférés engedélyezése</span>
              <p className="text-xs text-[#64748B]">Ha aktív, a cég felhasználói látják a Novitax adatokat az ügyfélportálon.</p>
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <Button type="submit" data-testid="company-save-btn" className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">
              {co.id ? 'Frissítés' : 'Létrehozás'}
            </Button>
            <Button type="button" variant="outline" onClick={() => { setShowEditor(false); setEditingCompany(null); }}
              className="border-[#E2E8F0]">Mégse</Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D]">Cégek</h3>
        <Button data-testid="add-company-btn"
          onClick={() => { setEditingCompany({}); setShowEditor(true); }}
          className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">
          <Plus size={16} className="mr-2" /> Új cég
        </Button>
      </div>

      {companies.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center text-[#64748B]">
          Még nincsenek cégek rögzítve.
        </div>
      ) : (
        <div className="space-y-4">
          {companies.map(co => {
            const assignedEmails = (co.assigned_users || []).map(uid => {
              const u = allUsers.find(u => u.email && (u.id === uid || u._id === uid));
              return u?.email || uid;
            });

            return (
              <div key={co.id} data-testid={`company-card-${co.id}`}
                className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
                {/* Header */}
                <div className="p-4 sm:p-5 flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-[#0B1B3D] text-base">{co.company_name}</h4>
                      {co.novitax_enabled ? (
                        <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium flex items-center gap-1">
                          <Wifi size={10} /> Novitax aktív
                        </span>
                      ) : (
                        <span className="text-xs bg-[#0B1B3D]/5 text-[#64748B] px-2 py-0.5 rounded">
                          Novitax inaktív
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center gap-4 text-xs text-[#64748B]">
                      <span>Adószám: <span className="font-mono">{co.tax_number}</span></span>
                      <span>Törzs: <span className="font-mono font-semibold text-[#0B1B3D]">{co.tax_number_core}</span></span>
                      {co.novitax_db_uid && <span>DB UID: <span className="font-mono">{co.novitax_db_uid.substring(0, 12)}...</span></span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    {co.novitax_enabled && co.novitax_db_uid && (
                      <Button variant="ghost" size="sm" data-testid={`test-novitax-${co.id}`}
                        onClick={() => handleTestConnection(co.id)}
                        disabled={testing}
                        className="text-[#64748B] hover:text-[#0B1B3D]" title="Novitax kapcsolat tesztelése">
                        {testing ? <Loader2 size={14} className="animate-spin" /> : <Wifi size={14} />}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm"
                      onClick={() => { setEditingCompany(co); setShowEditor(true); }}
                      className="text-[#64748B] hover:text-[#0B1B3D]">
                      <Pencil size={14} />
                    </Button>
                    <Button variant="ghost" size="sm"
                      onClick={() => onDelete(co.id)}
                      className="text-[#64748B] hover:text-red-600">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                {/* Test result */}
                {testResult && (
                  <div className={`mx-4 sm:mx-5 mb-3 p-3 rounded-lg text-sm ${
                    testResult.connected ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                  }`}>
                    {testResult.connected
                      ? <span className="flex items-center gap-1"><Check size={14} /> Kapcsolat sikeres (HTTP {testResult.status})</span>
                      : <span className="flex items-center gap-1"><X size={14} /> {testResult.error}</span>
                    }
                  </div>
                )}

                {/* Assigned Users */}
                <div className="px-4 sm:px-5 pb-4 pt-2 border-t border-[#E2E8F0] bg-[#FAFAFA]/50">
                  <p className="text-xs font-medium text-[#64748B] uppercase tracking-wider mb-2">Hozzárendelt felhasználók</p>
                  {assignedEmails.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {assignedEmails.map(email => (
                        <span key={email} className="inline-flex items-center gap-1 bg-white border border-[#E2E8F0] rounded-full px-3 py-1 text-xs text-[#1A1F2C]">
                          {email}
                          <button onClick={() => handleUnassign(co.id, email)} className="text-[#94A3B8] hover:text-red-500 transition-colors">
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-[#94A3B8] mb-3">Nincs hozzárendelt felhasználó.</p>
                  )}
                  <div className="flex gap-2">
                    <Input
                      value={assignEmail}
                      onChange={e => setAssignEmail(e.target.value)}
                      placeholder="felhasznalo@email.hu"
                      className="h-8 text-xs border-[#E2E8F0] flex-1"
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAssign(co.id))}
                    />
                    <Button size="sm" variant="outline" onClick={() => handleAssign(co.id)}
                      className="h-8 text-xs border-[#E2E8F0] hover:bg-[#0B1B3D] hover:text-white">
                      <UserPlus size={12} className="mr-1" /> Hozzáadás
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}


export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [messages, setMessages] = useState([]);
  const [legalPages, setLegalPages] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showArticleEditor, setShowArticleEditor] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showServiceEditor, setShowServiceEditor] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showCompanyEditor, setShowCompanyEditor] = useState(false);
  const [editingLegal, setEditingLegal] = useState(null);
  const [showLegalEditor, setShowLegalEditor] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, articlesRes, servicesRes, catRes, msgsRes, legalRes, companiesRes, usersRes] = await Promise.all([
        api.get('/admin/stats'),
        api.get('/articles'),
        api.get('/services'),
        api.get('/service-categories'),
        api.get('/admin/messages'),
        api.get('/legal'),
        api.get('/admin/companies'),
        api.get('/admin/users')
      ]);
      setStats(statsRes.data);
      setArticles(articlesRes.data);
      setServices(servicesRes.data);
      setCategories(catRes.data);
      setMessages(msgsRes.data);
      setLegalPages(legalRes.data);
      setCompanies(companiesRes.data);
      setAllUsers(usersRes.data);
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveArticle = async (data) => {
    if (editingArticle) {
      await api.put(`/admin/articles/${editingArticle.id}`, data);
    } else {
      await api.post('/admin/articles', data);
    }
    setShowArticleEditor(false);
    setEditingArticle(null);
    loadData();
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Biztosan törli ezt a cikket?')) return;
    await api.delete(`/admin/articles/${id}`);
    loadData();
  };

  const handleSaveService = async (data) => {
    if (editingService) {
      await api.put(`/admin/services/${editingService.id}`, data);
    } else {
      await api.post('/admin/services', data);
    }
    setShowServiceEditor(false);
    setEditingService(null);
    loadData();
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Biztosan törli ezt a szolgáltatást?')) return;
    await api.delete(`/admin/services/${id}`);
    loadData();
  };

  const handleSaveCategory = async (data) => {
    if (editingCategory) {
      await api.put(`/admin/service-categories/${editingCategory.id}`, data);
    } else {
      await api.post('/admin/service-categories', data);
    }
    setShowCategoryEditor(false);
    setEditingCategory(null);
    loadData();
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Biztosan törli ezt a kategóriát?')) return;
    await api.delete(`/admin/service-categories/${id}`);
    loadData();
  };

  const handleSaveCompany = async (data) => {
    if (editingCompany?.id) {
      await api.put(`/admin/companies/${editingCompany.id}`, data);
    } else {
      await api.post('/admin/companies', data);
    }
    setShowCompanyEditor(false);
    setEditingCompany(null);
    loadData();
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm('Biztosan törli ezt a céget?')) return;
    await api.delete(`/admin/companies/${id}`);
    loadData();
  };

  const handleSaveLegal = async (data) => {
    if (editingLegal) {
      await api.put(`/admin/legal/${editingLegal.id}`, data);
    }
    setShowLegalEditor(false);
    setEditingLegal(null);
    loadData();
  };

  const handleMarkRead = async (id) => {
    await api.put(`/admin/messages/${id}/read`);
    loadData();
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Biztosan törli ezt az üzenetet?')) return;
    await api.delete(`/admin/messages/${id}`);
    loadData();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-[#64748B]">Nincs hozzáférése ehhez az oldalhoz.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#FAFAFA]">
      {/* Admin Header */}
      <div className="bg-[#0B1B3D] py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[#C5A059] text-xs uppercase tracking-[0.2em] font-semibold">Admin</span>
              <h1 className="mt-1 text-xl sm:text-2xl font-['Outfit'] font-semibold text-white">Vezérlőpult</h1>
            </div>
            <Button onClick={handleLogout} variant="outline" size="sm"
              className="border-white/30 text-white hover:bg-white/10">
              <LogOut size={16} className="mr-2" /> Kijelentkezés
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            {[
              { label: 'Cikkek', value: stats.articles, icon: FileText },
              { label: 'Üzenetek', value: stats.messages, icon: MessageSquare },
              { label: 'Olvasatlan', value: stats.unread_messages, icon: EyeOff },
              { label: 'Felhasználók', value: stats.total_users, icon: Users },
              { label: 'Ügyfelek', value: stats.clients, icon: Briefcase },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="bg-white border border-[#E2E8F0] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={14} className="text-[#64748B]" />
                  <span className="text-xs text-[#64748B] uppercase tracking-wider">{label}</span>
                </div>
                <p className="text-2xl font-['Outfit'] font-semibold text-[#0B1B3D]">{value}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="articles" className="space-y-6">
          <TabsList className="bg-white border border-[#E2E8F0] p-1 rounded-lg flex-wrap">
            <TabsTrigger value="articles" data-testid="admin-tab-articles" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
              <FileText size={16} className="mr-2" /> Hírek
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="admin-tab-services" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
              <Briefcase size={16} className="mr-2" /> Csomagok
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="admin-tab-categories" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
              <Settings size={16} className="mr-2" /> Kategóriák
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
              <MessageSquare size={16} className="mr-2" /> Üzenetek
            </TabsTrigger>
            <TabsTrigger value="legal" data-testid="admin-tab-legal" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
              <Scale size={16} className="mr-2" /> Jogi oldalak
            </TabsTrigger>
            <TabsTrigger value="companies" data-testid="admin-tab-companies" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
              <Building2 size={16} className="mr-2" /> Cégek
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="admin-tab-documents" className="data-[state=active]:bg-[#0B1B3D] data-[state=active]:text-white rounded-md">
              <FolderOpen size={16} className="mr-2" /> Dokumentumok
            </TabsTrigger>
          </TabsList>

          {/* ARTICLES TAB */}
          <TabsContent value="articles">
            {showArticleEditor ? (
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 sm:p-8">
                <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D] mb-4">
                  {editingArticle ? 'Cikk szerkesztése' : 'Új cikk'}
                </h3>
                <ArticleEditor
                  article={editingArticle}
                  onSave={handleSaveArticle}
                  onCancel={() => { setShowArticleEditor(false); setEditingArticle(null); }}
                />
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D]">Cikkek</h3>
                  <Button data-testid="add-article-btn"
                    onClick={() => { setEditingArticle(null); setShowArticleEditor(true); }}
                    className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">
                    <Plus size={16} className="mr-2" /> Új cikk
                  </Button>
                </div>
                <div className="space-y-3">
                  {articles.length === 0 ? (
                    <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center text-[#64748B]">
                      Még nincsenek cikkek.
                    </div>
                  ) : articles.map(article => (
                    <div key={article.id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-[#0B1B3D] truncate">{article.title}</h4>
                        <p className="text-xs text-[#64748B] mt-1">
                          {new Date(article.created_at).toLocaleDateString('hu-HU')} | /{article.slug}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button variant="ghost" size="sm"
                          onClick={() => { setEditingArticle(article); setShowArticleEditor(true); }}
                          className="text-[#64748B] hover:text-[#0B1B3D]">
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm"
                          onClick={() => handleDeleteArticle(article.id)}
                          className="text-[#64748B] hover:text-red-600">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* SERVICES TAB */}
          <TabsContent value="services">
            {showServiceEditor ? (
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 sm:p-8">
                <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D] mb-4">
                  {editingService ? 'Szolgáltatás szerkesztése' : 'Új szolgáltatás'}
                </h3>
                <ServiceEditor
                  service={editingService}
                  onSave={handleSaveService}
                  onCancel={() => { setShowServiceEditor(false); setEditingService(null); }}
                />
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D]">Szolgáltatások</h3>
                  <Button data-testid="add-service-btn"
                    onClick={() => { setEditingService(null); setShowServiceEditor(true); }}
                    className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">
                    <Plus size={16} className="mr-2" /> Új szolgáltatás
                  </Button>
                </div>
                <div className="space-y-3">
                  {services.map(svc => (
                    <div key={svc.id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-[#0B1B3D]">{svc.name}</h4>
                          <span className="text-xs bg-[#0B1B3D]/5 text-[#64748B] px-2 py-0.5 rounded">{svc.tier}</span>
                          {svc.highlighted && <span className="text-xs bg-[#C5A059]/10 text-[#C5A059] px-2 py-0.5 rounded">Kiemelt</span>}
                        </div>
                        <p className="text-xs text-[#64748B] mt-1 truncate">{svc.description}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button variant="ghost" size="sm"
                          onClick={() => { setEditingService(svc); setShowServiceEditor(true); }}
                          className="text-[#64748B] hover:text-[#0B1B3D]">
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm"
                          onClick={() => handleDeleteService(svc.id)}
                          className="text-[#64748B] hover:text-red-600">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* CATEGORIES TAB */}
          <TabsContent value="categories">
            {showCategoryEditor ? (
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 sm:p-8">
                <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D] mb-4">
                  {editingCategory ? 'Kategória szerkesztése' : 'Új kategória'}
                </h3>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = {
                    slug: editingCategory?.slug || editingCategory?.title?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || '',
                    title: editingCategory?.title || '',
                    description: editingCategory?.description || '',
                    content: editingCategory?.content || '',
                    image: editingCategory?.image || '',
                    has_packages: editingCategory?.has_packages || false,
                    order: parseInt(editingCategory?.order) || 0
                  };
                  await handleSaveCategory(formData);
                }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#1A1F2C] font-medium">Cím *</Label>
                      <Input
                        value={editingCategory?.title || ''}
                        onChange={e => setEditingCategory({ ...editingCategory, title: e.target.value })}
                        required
                        className="mt-1.5 border-[#E2E8F0]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1F2C] font-medium">Sorrend</Label>
                      <Input
                        type="number"
                        value={editingCategory?.order || 0}
                        onChange={e => setEditingCategory({ ...editingCategory, order: e.target.value })}
                        className="mt-1.5 border-[#E2E8F0]"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#1A1F2C] font-medium">Leírás</Label>
                    <Textarea
                      value={editingCategory?.description || ''}
                      onChange={e => setEditingCategory({ ...editingCategory, description: e.target.value })}
                      rows={2}
                      className="mt-1.5 border-[#E2E8F0]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#1A1F2C] font-medium">Tartalom (HTML)</Label>
                    <Textarea
                      value={editingCategory?.content || ''}
                      onChange={e => setEditingCategory({ ...editingCategory, content: e.target.value })}
                      rows={6}
                      className="mt-1.5 border-[#E2E8F0] font-mono text-sm"
                      placeholder="Tartalom vagy 'Feltöltés alatt'"
                    />
                  </div>
                  <ImageUploader
                    value={editingCategory?.image || ''}
                    onChange={(url) => setEditingCategory({ ...editingCategory, image: url })}
                    label="Kategória kép"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingCategory?.has_packages || false}
                      onChange={e => setEditingCategory({ ...editingCategory, has_packages: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-[#1A1F2C]">Könyvelési csomagok megjelenítése (Bronz/Ezüst/Arany/VIP)</span>
                  </div>
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">
                      {editingCategory?.id ? 'Frissítés' : 'Létrehozás'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => { setShowCategoryEditor(false); setEditingCategory(null); }}
                      className="border-[#E2E8F0]">Mégse</Button>
                  </div>
                </form>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D]">Szolgáltatási kategóriák</h3>
                  <Button data-testid="add-category-btn"
                    onClick={() => { setEditingCategory({}); setShowCategoryEditor(true); }}
                    className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">
                    <Plus size={16} className="mr-2" /> Új kategória
                  </Button>
                </div>
                <div className="space-y-3">
                  {categories.map(cat => (
                    <div key={cat.id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-[#0B1B3D]">{cat.title}</h4>
                          <span className="text-xs bg-[#0B1B3D]/5 text-[#64748B] px-2 py-0.5 rounded">/{cat.slug}</span>
                          {cat.has_packages && <span className="text-xs bg-[#C5A059]/10 text-[#C5A059] px-2 py-0.5 rounded">Csomagok</span>}
                        </div>
                        <p className="text-xs text-[#64748B] mt-1 truncate">{cat.description}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button variant="ghost" size="sm"
                          onClick={() => { setEditingCategory(cat); setShowCategoryEditor(true); }}
                          className="text-[#64748B] hover:text-[#0B1B3D]">
                          <Pencil size={14} />
                        </Button>
                        <Button variant="ghost" size="sm"
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="text-[#64748B] hover:text-red-600">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* MESSAGES TAB */}
          <TabsContent value="messages">
            <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D] mb-4">Üzenetek</h3>
            <div className="space-y-3">
              {messages.length === 0 ? (
                <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center text-[#64748B]">
                  Nincsenek üzenetek.
                </div>
              ) : messages.map(msg => (
                <div key={msg.id} className={`bg-white border rounded-lg p-4 ${msg.read ? 'border-[#E2E8F0]' : 'border-[#C5A059] bg-[#C5A059]/5'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-[#0B1B3D]">{msg.name}</h4>
                        {!msg.read && <span className="text-xs bg-[#C5A059] text-white px-2 py-0.5 rounded">Új</span>}
                      </div>
                      <p className="text-xs text-[#64748B] mt-1">{msg.email} {msg.phone && `| ${msg.phone}`}</p>
                      <p className="text-sm text-[#1A1F2C] mt-2">{msg.message}</p>
                      <p className="text-xs text-[#64748B] mt-2">
                        {new Date(msg.created_at).toLocaleString('hu-HU')}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-4">
                      {!msg.read && (
                        <Button variant="ghost" size="sm" onClick={() => handleMarkRead(msg.id)}
                          className="text-[#64748B] hover:text-[#0B1B3D]" title="Olvasottként jelölés">
                          <Eye size={14} />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteMessage(msg.id)}
                        className="text-[#64748B] hover:text-red-600">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* LEGAL PAGES TAB */}
          <TabsContent value="legal">
            {showLegalEditor ? (
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 sm:p-8">
                <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D] mb-4">
                  Jogi oldal szerkesztése: {editingLegal?.title}
                </h3>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  await handleSaveLegal({ title: editingLegal.title, content: editingLegal.content });
                }} className="space-y-4">
                  <div>
                    <Label className="text-[#1A1F2C] font-medium">Cím</Label>
                    <Input
                      value={editingLegal?.title || ''}
                      onChange={e => setEditingLegal({ ...editingLegal, title: e.target.value })}
                      className="mt-1.5 border-[#E2E8F0]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#1A1F2C] font-medium">Tartalom (HTML)</Label>
                    <Textarea
                      value={editingLegal?.content || ''}
                      onChange={e => setEditingLegal({ ...editingLegal, content: e.target.value })}
                      rows={10}
                      className="mt-1.5 border-[#E2E8F0] font-mono text-sm"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" className="bg-[#0B1B3D] hover:bg-[#162B56] text-white">Mentés</Button>
                    <Button type="button" variant="outline" onClick={() => { setShowLegalEditor(false); setEditingLegal(null); }}
                      className="border-[#E2E8F0]">Mégse</Button>
                  </div>
                </form>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D] mb-4">Jogi oldalak</h3>
                <div className="space-y-3">
                  {legalPages.map(page => (
                    <div key={page.id} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-[#0B1B3D]">{page.title}</h4>
                        <p className="text-xs text-[#64748B] mt-1">/{page.slug}</p>
                      </div>
                      <Button variant="ghost" size="sm"
                        onClick={() => { setEditingLegal(page); setShowLegalEditor(true); }}
                        className="text-[#64748B] hover:text-[#0B1B3D]">
                        <Pencil size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* COMPANIES TAB */}
          <TabsContent value="companies">
            <CompaniesTab
              companies={companies}
              allUsers={allUsers}
              onSave={handleSaveCompany}
              onDelete={handleDeleteCompany}
              onReload={loadData}
              editingCompany={editingCompany}
              setEditingCompany={setEditingCompany}
              showEditor={showCompanyEditor}
              setShowEditor={setShowCompanyEditor}
            />
          </TabsContent>

          {/* DOCUMENTS TAB */}
          <TabsContent value="documents">
            <AdminDocuments />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
